import EsimProvider from './esim/provider.js';
import EsimStore from './esim/store.js';
import Account from './auth/account.js';
import i18n from './utils/i18n.js';
import toast from './utils/toast.js';
import config from './utils/config.js';
import { initEsim } from './esim/main.js';
import { initCart } from './cart/main.js';
import { initAuth } from './auth/main.js';
import compatibilityChecker from './utils/compatibility.js';
import { hideDropdown, adjustBodyBackgroundSize } from './utils/utils.js';
import { trackPurchase, trackCheckoutCanceled, trackLanguageChange, trackFAQOpen } from './utils/analytics.js';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'flag-icons/css/flag-icons.min.css';

const provider = new EsimProvider();
const store = new EsimStore(provider);
const account = new Account();

// Login by link: if URL has ?itoken=..., save as session (e.g. from admin) and clean URL
(function checkLoginByLink() {
    if (typeof account.loginWithToken !== 'function') return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('itoken');
    if (token && account.loginWithToken(token)) {
        params.delete('itoken');
        const search = params.toString();
        const cleanUrl = window.location.pathname + (search ? '?' + search : '') + window.location.hash;
        window.history.replaceState({}, '', cleanUrl);
    }
})();

const sidebarOverlay = document.getElementById('sidebarOverlay');
const cartSidebar = document.getElementById('cartSidebar');
const cartToggleButton = document.getElementById('cartToggleButton');
const cartBody = document.getElementById('cartBody');
const cartBuyButton = document.getElementById('cartBuyButton');
const cartBadge = document.getElementById('cartBadge');

const packageDetailsSidebar = document.getElementById('packageDetailsSidebar');
const compatibilitySidebar = document.getElementById('compatibilitySidebar');
const compatibilityToggleButton = document.getElementById('compatibilityToggleButton');
const installationSidebar = document.getElementById('installationSidebar');
const installationToggleButton = document.getElementById('installationToggleButton');
const authMain = initAuth(account, {
    sidebarOverlay
}, {
    renderCart: null,
    onLogout: null
});
const cartUI = initCart(store.cart, {
    cartSidebar,
    cartToggleButton,
    cartBody,
    cartBuyButton,
    cartBadge,
    sidebarOverlay
}, {
    account: account,
    onCheckout: async (cartItems) => {
        const currentUser = account.getCurrentUser();
        const baseUrl = config.api.checkout;
        const response = await fetch(baseUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                userId: currentUser?.id || null,
                userEmail: currentUser?.email || null,
                items: cartItems.map(item => ({
                    packageCode: item.packageCode,
                    price: item.price,
                    quantity: item.quantity,
                    name: item.name,
                    ...(item.esimTranNo && { esimTranNo: item.esimTranNo })
                }))
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.details || errorData.error || 'Failed to create checkout session');
        }

        const session = await response.json();

        if (session.error) {
            throw new Error(session.details || session.error);
        }

        if (session.url) {
            window.location.href = session.url;
        } else if (session.id) {
            window.location.href = `https://checkout.stripe.com/c/pay/${session.id}`;
        } else {
            throw new Error('No checkout URL or session ID received from server');
        }
    },
    onOpenRegister: () => {
        return true;
    },
    checkOtherSidebars: () => {
        return !packageDetailsSidebar.classList.contains('hidden') ||
               authMain.hasOpenSidebars();
    }
});

const esimMain = initEsim(store, {
    sidebarOverlay
}, {
    renderCart: () => cartUI.renderCart(),
    openCart: () => cartUI.openCart()
});

authMain.setCallbacks({
    renderCart: () => cartUI.renderCart(),
    onLogout: () => {
        cartUI.renderCart();
    },
    store: store,
    showTopupPlans: esimMain.showTopupPlans
});

document.addEventListener('click', (e) => {
    if (e.target.closest('.sidebar-close')) {
        const closeButton = e.target.closest('.sidebar-close');
        const sidebar = closeButton.closest('.sidebar');
        
        if (!sidebar) return;
        
        const sidebarId = sidebar.id;
        
        if (sidebarId === 'cartSidebar') {
            cartUI.closeCart();
        } else if (sidebarId === 'packageDetailsSidebar') {
            esimMain.closePackageDetailsSidebarFunc();
            const hasOpenSidebar = !cartSidebar.classList.contains('hidden') ||
                                   authMain.hasOpenSidebars();
            if (!hasOpenSidebar) {
                sidebarOverlay.classList.add('hidden');
                document.body.style.overflow = '';
            }
        } else if (sidebarId === 'authSidebar') {
            authMain.closeAuthSidebarFunc(true);
        } else if (sidebarId === 'userEsimsSidebar') {
            authMain.closeUserEsimsSidebarFunc();
        } else if (sidebarId === 'editProfileSidebar') {
            authMain.closeEditProfileSidebarFunc();
        } else if (sidebarId === 'compatibilitySidebar') {
            closeCompatibilitySidebar();
        } else if (sidebarId === 'installationSidebar') {
            closeInstallationSidebar();
        }
    }
});

sidebarOverlay.addEventListener('click', (e) => {
    if (!cartSidebar.classList.contains('hidden')) {
        cartUI.closeCart();
    } else if (!packageDetailsSidebar.classList.contains('hidden')) {
        esimMain.closePackageDetailsSidebarFunc();
        const hasOpenSidebar = !cartSidebar.classList.contains('hidden') ||
                               authMain.hasOpenSidebars();
        if (!hasOpenSidebar) {
            sidebarOverlay.classList.add('hidden');
    document.body.style.overflow = '';
        }
    } else if (authMain.hasOpenSidebars()) {
        authMain.closeAnyAuthSidebar();
    } else if (!compatibilitySidebar.classList.contains('hidden')) {
        closeCompatibilitySidebar();
    } else if (!installationSidebar.classList.contains('hidden')) {
        closeInstallationSidebar();
}
});

function checkStripeRedirect() {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const pathname = window.location.pathname;

    // Check for cancel route
    if (pathname === '/cancel') {
        window.history.replaceState({}, document.title, '/');
        const cartItems = store.cart.getCart();
        if (cartItems.length > 0) {
            trackCheckoutCanceled(cartItems);
        }
        toast.error(i18n.t('paymentCanceled'));
        return;
    }
    
    // Check for success route with session_id
    if (pathname === '/success' && sessionId) {
        window.history.replaceState({}, document.title, '/');
        const cartItems = store.cart.getCart();
        const totalValue = cartItems.reduce((sum, item) => {
            return sum + (parseFloat(item.price) * item.quantity);
        }, 0);
        
        trackPurchase(sessionId, cartItems, totalValue);
        
        store.clearCart();
        cartUI.renderCart();
        // Toast permanente (duration = 0 significa que no se cierra automáticamente)
        toast.success(i18n.t('paymentSuccessful'), 0);
    }
}

function initCompatibilityChecker() {
    const deviceSearchInput = document.getElementById('deviceSearch');
    const deviceDropdown = document.getElementById('deviceAutocompleteDropdown');
    const deviceResults = document.getElementById('deviceSearchResults');

    if (!deviceSearchInput || !deviceDropdown || !deviceResults) return;

    let searchTimeout;

    deviceSearchInput.addEventListener('input', (e) => {
        const query = e.target.value.trim();
        
        clearTimeout(searchTimeout);
        
        if (query.length < 2) {
            hideDropdown(deviceDropdown);
            deviceResults.classList.add('hidden');
            return;
        }

        searchTimeout = setTimeout(() => {
            const matches = compatibilityChecker.searchDevices(query);
            
            if (matches.length > 0) {
                renderDeviceDropdown(matches);
                deviceDropdown.classList.remove('hidden');
            } else {
                hideDropdown(deviceDropdown);
            }
        }, 300);
    });

    deviceSearchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
    e.preventDefault();
            const query = e.target.value.trim();
            if (query.length >= 2) {
                checkDeviceCompatibility(query);
            }
        }
});

    document.addEventListener('click', (e) => {
        if (!deviceSearchInput.contains(e.target) && !deviceDropdown.contains(e.target)) {
            hideDropdown(deviceDropdown);
    }
});

    function renderDeviceDropdown(devices) {
        deviceDropdown.innerHTML = devices.map(device => `
            <div class="autocomplete-item d-flex align-items-center gap-2 p-2" data-device="${device}">
                <i class="bi bi-phone"></i>
                <div class="autocomplete-item-content flex-1">
                    <div class="autocomplete-item-name">${device}</div>
                </div>
            </div>
        `).join('');

        deviceDropdown.querySelectorAll('.autocomplete-item').forEach(item => {
            item.addEventListener('click', () => {
                const device = item.dataset.device;
                deviceSearchInput.value = device;
                hideDropdown(deviceDropdown);
                checkDeviceCompatibility(device);
            });
        });
    }

    function checkDeviceCompatibility(deviceName) {
        const result = compatibilityChecker.checkCompatibility(deviceName);
        renderDeviceResult(result);
    }

    function renderDeviceResult(result) {
        if (!result.device) {
            deviceResults.innerHTML = `
                <div class="device-result-card rounded-2">
                    <div class="device-result-message text-gray mt-1">${result.message || i18n.t('deviceNotFound')}</div>
                </div>
            `;
        } else {
            const statusClass = result.compatible ? 'compatible' : 'not-compatible';
            const statusText = result.compatible ? i18n.t('deviceCompatible') : i18n.t('deviceNotCompatible');
            
            deviceResults.innerHTML = `
                <div class="device-result-card rounded-2 ${statusClass} mb-2">
                    <div class="device-result-header mb-1">
                        <div class="device-result-name">${result.device}</div>
                        <div class="device-result-status rounded-2 ${statusClass}">${statusText}</div>
                    </div>
                    <div class="device-result-message text-gray mt-1">${result.message}</div>
                </div>
            `;
        }
        
        deviceResults.classList.remove('hidden');
    }
}

function openCompatibilitySidebar() {
    compatibilitySidebar.classList.remove('hidden');
    sidebarOverlay.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
}

function closeCompatibilitySidebar() {
    compatibilitySidebar.classList.add('hidden');
    const hasOpenSidebar = !cartSidebar.classList.contains('hidden') ||
                           !packageDetailsSidebar.classList.contains('hidden') ||
                           authMain.hasOpenSidebars();
    if (!hasOpenSidebar) {
        sidebarOverlay.classList.add('hidden');
    document.body.style.overflow = '';
    }
}

if (compatibilityToggleButton) {
    compatibilityToggleButton.addEventListener('click', (e) => {
        e.preventDefault();
        openCompatibilitySidebar();
});
}

function openInstallationSidebar() {
    installationSidebar.classList.remove('hidden');
    sidebarOverlay.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
}

function closeInstallationSidebar() {
    installationSidebar.classList.add('hidden');
    const hasOpenSidebar = !cartSidebar.classList.contains('hidden') ||
                           !packageDetailsSidebar.classList.contains('hidden') ||
                           !compatibilitySidebar.classList.contains('hidden') ||
                           authMain.hasOpenSidebars();
    if (!hasOpenSidebar) {
        sidebarOverlay.classList.add('hidden');
    document.body.style.overflow = '';
    }
}

if (installationToggleButton) {
    installationToggleButton.addEventListener('click', (e) => {
    e.preventDefault();
        openInstallationSidebar();
    });
}

const languageEsButton = document.getElementById('languageEs');
const languageEnButton = document.getElementById('languageEn');

function updateLanguageButtons() {
    const currentLang = i18n.getLanguage();
    if (languageEsButton && languageEnButton) {
        languageEsButton.classList.remove('active');
        languageEnButton.classList.remove('active');
        
        if (currentLang === 'es') {
            languageEsButton.classList.add('active');
        } else {
            languageEnButton.classList.add('active');
        }
    }
}

window.updateLanguageButtons = updateLanguageButtons;

if (languageEsButton) {
    languageEsButton.addEventListener('click', async () => {
        i18n.setLanguage('es');
        trackLanguageChange('es');
        updateLanguageButtons();
        if (esimMain && esimMain.loadCountries) {
            await esimMain.loadCountries();
        }
        if (esimMain && esimMain.refreshCurrentSections) {
            esimMain.refreshCurrentSections();
        }
        if (cartUI && cartUI.renderCart) {
            cartUI.renderCart();
        }
    });
}

if (languageEnButton) {
    languageEnButton.addEventListener('click', async () => {
        i18n.setLanguage('en');
        trackLanguageChange('en');
        updateLanguageButtons();
        if (esimMain && esimMain.loadCountries) {
            await esimMain.loadCountries();
        }
        if (esimMain && esimMain.refreshCurrentSections) {
            esimMain.refreshCurrentSections();
        }
        if (cartUI && cartUI.renderCart) {
            cartUI.renderCart();
        }
    });
}

updateLanguageButtons();

const currentYearElement = document.getElementById('currentYear');
if (currentYearElement) {
    currentYearElement.textContent = new Date().getFullYear();
}

const supportEmailElement = document.getElementById('supportEmail');
if (supportEmailElement) {
    supportEmailElement.addEventListener('click', async (e) => {
        e.preventDefault();
        const email = 'support@ilovej1.com';
        
        try {
            await navigator.clipboard.writeText(email);
            toast.success(i18n.t('emailCopied'));
        } catch (error) {
            // Fallback para navegadores que no soportan clipboard API
            const textArea = document.createElement('textarea');
            textArea.value = email;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                toast.success(i18n.t('emailCopied'));
            } catch (err) {
                console.error('Failed to copy email:', err);
                toast.error('Failed to copy email');
            } finally {
                document.body.removeChild(textArea);
            }
        }
    });
}

// FAQ Toggle functionality
const faqQuestions = document.querySelectorAll('.faq-question');
faqQuestions.forEach(question => {
    question.addEventListener('click', () => {
        const faqItem = question.closest('.faq-item');
        const faqAnswer = faqItem.querySelector('.faq-answer');
        const isActive = faqItem.classList.contains('active');
        const faqNumber = question.getAttribute('data-faq');
        
        // Close all other FAQ items
        document.querySelectorAll('.faq-item').forEach(item => {
            if (item !== faqItem) {
                item.classList.remove('active');
                item.querySelector('.faq-answer').classList.add('hidden');
            }
        });
        
        // Toggle current FAQ item
        if (isActive) {
            faqItem.classList.remove('active');
            faqAnswer.classList.add('hidden');
        } else {
            faqItem.classList.add('active');
            faqAnswer.classList.remove('hidden');
            if (faqNumber) {
                trackFAQOpen(parseInt(faqNumber));
            }
        }
    });
});

i18n.updatePage();
checkStripeRedirect();
initCompatibilityChecker();

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', adjustBodyBackgroundSize);
    } else {
    adjustBodyBackgroundSize();
}

let resizeTimeout;
window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(adjustBodyBackgroundSize, 100);
});

window.addEventListener('orientationchange', () => {
    setTimeout(adjustBodyBackgroundSize, 100);
});

